import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('taxAdvisor');
export interface TaxAdvisorInfoRec {
  id: string;
  userId: string;
  firmName?: string;
  advisorName?: string;
  credentials?: string;
  officeAddress?: string;
  officePhone?: string;
  cellPhone?: string;
  email?: string;
  website?: string;
  licenseNumber?: string;
  hourlyRate?: number;
  annualFee?: number;
  servicesProvided?: string;
  notes?: string;
  isLocked?: boolean;
}

class TaxAdvisorInfoService {
  async getAllTaxAdvisorInfo(targetUserId?: string): Promise<TaxAdvisorInfoRec[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await http.get(`/api/tax-advisor-info${params}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch tax advisor information');
    }

    return response.data.data || [];
  }

  async getTaxAdvisorInfo(targetUserId?: string): Promise<TaxAdvisorInfoRec[]> {
    return this.getAllTaxAdvisorInfo(targetUserId);
  }

  async createTaxAdvisorInfo(data: Partial<TaxAdvisorInfoRec>): Promise<TaxAdvisorInfoRec> {
    const response = await http.post('/api/tax-advisor-info', data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save tax advisor information');
    }

    return response.data.data;
  }

  async updateTaxAdvisorInfo(id: string, data: Partial<TaxAdvisorInfoRec>): Promise<TaxAdvisorInfoRec> {
    const response = await http.patch(`/api/tax-advisor-info/${id}`, data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update tax advisor information');
    }

    return response.data.data;
  }

  async deleteTaxAdvisorInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/tax-advisor-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete tax advisor information');
    }
  }

  async lockTaxAdvisorInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for tax advisor ID:', id);
    const response = await http.post(`/api/tax-advisor-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock tax advisor information');
    }
  }

  async unlockTaxAdvisorInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/tax-advisor-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock tax advisor information');
    }
  }

  async getTaxAdvisorAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/tax-advisor-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const taxAdvisorInfoService = new TaxAdvisorInfoService();